import { motion } from "framer-motion";
import { fadeUp, stagger } from "@/lib/anim";
import Reveal from "@/components/Reveal";

const chapters = [
  {
    n: "01",
    title: "Make asking for help feel safe.",
    body: "Stigma keeps students silent. By stripping away names and exposure, we lower the cost of reaching out — so the first message is never the hardest.",
  },
  {
    n: "02",
    title: "Keep every conversation protected.",
    body: "Support should never come with risk. Moderated, encrypted channels mean each exchange stays confidential, monitored for safety, and firmly on track.",
  },
  {
    n: "03",
    title: "Meet students where they already are.",
    body: "No appointments to dread, no waiting rooms to sit in. Just a private line — open around the clock, ready whenever the weight gets heavy.",
  },
  {
    n: "04",
    title: "Help institutions care, responsibly.",
    body: "Anonymized, aggregate insight lets schools understand wellbeing trends and act early — without ever compromising an individual's privacy.",
  },
];

export const Manifesto = () => {
  return (
    <section
      id="vision"
      className="py-24 md:py-40 px-6 md:px-12"
      data-testid="manifesto-section"
    >
      <div className="mx-auto max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-28">
              <Reveal>
                <p className="text-xs uppercase tracking-[0.24em] font-semibold text-primary mb-6">
                  Our goals & vision
                </p>
              </Reveal>
              <Reveal delay={0.05}>
                <h2 className="font-serif font-light tracking-tight text-foreground text-4xl lg:text-5xl leading-[1.05] text-balance">
                  A quiet promise, in four parts.
                </h2>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-6 text-muted-foreground leading-relaxed max-w-sm">
                  Everything we build answers a single question: how do we make real
                  support reachable — without ever putting a student at risk?
                </p>
              </Reveal>
            </div>
          </div>

          <motion.div
            variants={stagger(0.12)}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            className="lg:col-span-8 flex flex-col"
          >
            {chapters.map((c) => (
              <motion.div
                key={c.n}
                variants={fadeUp}
                data-testid={`manifesto-chapter-${c.n}`}
                className="group grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-6 sm:gap-10 py-10 md:py-12 border-t border-border first:border-t-0"
              >
                <div className="font-serif text-6xl md:text-7xl font-light text-accent/70 leading-none group-hover:text-accent transition-colors duration-500">
                  {c.n}
                </div>
                <div className="max-w-2xl">
                  <h3 className="font-serif text-2xl md:text-3xl text-foreground mb-4 tracking-tight">
                    {c.title}
                  </h3>
                  <p className="text-lg leading-relaxed text-muted-foreground">
                    {c.body}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Manifesto;
